import { computed, ref } from 'vue';

import { defineStore } from 'pinia';

// Central registry that lets the global header "Guided tour" modal (WP-929 / WP-987) launch
// a tour that is actually owned by a currently-mounted OLAP host (OlapPage / OlapGrid /
// MetricsByDatePanel). The header can't render <OlapTour> itself — the chart/grid tours need
// a live gridApi / chart DOM — so each host registers { start, isAvailable } on mount and the
// modal dispatches to it. Registration doubles as the "is an OLAP page on screen?" signal
// that gates the header button: no host registered → no button, so the "?" only appears on
// OLAP pages without any per-route bookkeeping.
export const useOlapTourLauncher = defineStore('olapTourLauncher', () => {
  // tourId -> { start, isAvailable }. A plain object reassigned on change so the computed
  // signals below re-evaluate (Pinia setup store, same pattern as devOlapSettings).
  const registrations = ref({});

  const register = (id, { start, isAvailable } = {}) => {
    if (!id || typeof start !== 'function') return;
    registrations.value = {
      ...registrations.value,
      [id]: { start, isAvailable: typeof isAvailable === 'function' ? isAvailable : () => true },
    };
  };

  // Only drop the entry if this owner still holds it — a route swap can mount the next page's
  // host before the previous one unmounts, and both use the same tour id (e.g. 'olap-grid').
  // The owner check keeps a late unmount from clobbering the fresh registration.
  const unregister = (id, owner) => {
    const entry = registrations.value[id];
    if (!entry) return;
    if (owner && entry.start !== owner) return;
    const next = { ...registrations.value };
    delete next[id];
    registrations.value = next;
  };

  // At least one OLAP host is mounted → the header shows its "?" button.
  const isOlapActive = computed(() => Object.keys(registrations.value).length > 0);

  const isRegistered = (id) => !!registrations.value[id];

  // A registered tour may still be momentarily unavailable (e.g. the grid's grouping bar is
  // hidden). Unregistered → not available at all. The modal disables such rows.
  const isAvailable = (id) => {
    const entry = registrations.value[id];
    if (!entry) return false;
    try {
      return !!entry.isAvailable();
    } catch {
      return true;
    }
  };

  // Fire the owning host's start(). Returns false (no-op) if nothing is registered for the id.
  const launch = (id) => {
    const entry = registrations.value[id];
    if (!entry) return false;
    entry.start();
    return true;
  };

  return { register, unregister, isOlapActive, isRegistered, isAvailable, launch };
});
